import { getId, isDefined } from 'services/utils'
import {
  findSingleFacet,
  findMultipleFacets,
  bundleFacetIds,
} from 'ui-shared/Facets'
import { uploadOrKeepFile } from '../../common'
import { parseDoc } from '../utils'
import { APIFacet, parseFacet } from '../facets'
import {
  productWithUpsellingParse,
  productWithUpsellingWrite,
} from '../productWithUpsellings'
import {
  locationArrangementsParse,
  locationArrangementsWrite,
} from './arrangement'
import { byRulesPricingParse, byRulesPricingWrite } from './locationPricing'
import { LocationToApi } from './types/api/request-models'
import { APILocation } from './types/api/response-models'
import { Location } from './types/internal/location-models'

// ============
// UTILS
// ============

const LOCATION_FACET_REGEX = /^productFilter:Location:(.*)/

const getLocationZone = (facets: APIFacet[]): string | undefined =>
  facets.map((x) => x.code.match(LOCATION_FACET_REGEX)).find((x) => !!x)?.[1]

// ============
// PARSE
// ============

const locationParse = ({
  features,
  planimetry,
  ...res
}: APILocation): Location => {
  const equipments = (features ?? [])
    .filter((f) => f.category === 'equipment')
    .map(getId)
  const characteristics = (features ?? [])
    .filter((f) => f.category === 'characteristic')
    .map(getId)

  const facets = res.facets.map(parseFacet)
  const filterLocationFacet = findSingleFacet(
    facets,
    'productFilter',
    'Location'
  )

  const tagFacets = findMultipleFacets(facets, 'tag')
  const eventTypeFacet = findMultipleFacets(
    facets,
    'productFilter',
    'EventType'
  )

  return {
    ...res,
    ...productWithUpsellingParse(res),
    planimetry: planimetry.map(parseDoc).filter(isDefined),
    locationZone: getLocationZone(res.facets),
    supplier: res.supplier?.id,
    supplierName: res.supplier?.name ?? '---',
    equipments,
    characteristics,
    filterLocationFacet,
    tagFacets,
    arrangements: locationArrangementsParse(res.arrangements),
    eventTypeFacet,
    pricing: res.pricing.map(byRulesPricingParse),
  }
}

// ============
// WRITE
// ============

const locationWrite = async (req: Location): Promise<LocationToApi> => {
  const {
    planimetry = [],
    supplierName,
    equipments = [],
    characteristics = [],
    filterLocationFacet,
    tagFacets = [],
    arrangements,
    eventTypeFacet = [],
    defaultHoursBefore,
    defaultHoursAfter,
    availableFrom,
    availableTo,
    ...rest
  } = req

  const transformedArrangements = locationArrangementsWrite(arrangements)

  const facets = bundleFacetIds(
    filterLocationFacet,
    ...tagFacets,
    ...eventTypeFacet
  )

  const planimetryUpl = await uploadOrKeepFile(planimetry)
  const features = [...equipments, ...characteristics]

  return {
    ...(await productWithUpsellingWrite(rest)),
    planimetry: planimetryUpl,
    features,
    facets,
    defaultHoursBefore,
    defaultHoursAfter,
    availableFrom,
    availableTo,
    arrangements: transformedArrangements,
    pricing: req.pricing.map(byRulesPricingWrite),
  }
}

// ============
// CFG
// ============

const apiCfg = {
  endpoint: 'location',
  parse: locationParse,
  write: locationWrite,
}

export default apiCfg
